// SPDX-License-Identifier: MIT
// Desktop version history — durable, backed by a local JSON file via the Rust
// get_versions / set_versions commands (mirrors conversation-store / projects).
// The web app keeps version snapshots in public.project_versions ; on desktop the
// data-bridge appends them to a flat local file (all projects, like the table).
// Read-through / write-through, no module cache. "Single-user" does NOT mean
// single-flow: cloud-sync's pull loop appends versions while a save may append
// too, so the read-modify-write below is serialized (same lost-update guard as
// projects.json) — an unserialized append dropped pulled versions.
import type { ProjectVersionRow } from '../projects/api';
import { tauriInvoke } from './tauri';

export async function readAllVersions(): Promise<ProjectVersionRow[]> {
  const raw = await tauriInvoke()('get_versions');
  if (typeof raw !== 'string' || raw.length === 0) return [];
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as ProjectVersionRow[]) : [];
  } catch {
    return [];
  }
}

export async function writeAllVersions(rows: ProjectVersionRow[]): Promise<void> {
  await tauriInvoke()('set_versions', { json: JSON.stringify(rows) });
}

// Serialize appends so concurrent callers each see the freshest versions.json.
let versionsOpChain: Promise<unknown> = Promise.resolve();
export async function appendVersion(row: ProjectVersionRow): Promise<void> {
  const run = versionsOpChain.then(async () => {
    const all = await readAllVersions();
    all.push(row);
    await writeAllVersions(all);
  });
  versionsOpChain = run.then(() => undefined, () => undefined);
  return run;
}

// All versions of a project, newest-first — mirrors the web
// listProjectVersions ordering (version_number DESC).
export async function listVersionsForProject(projectId: string): Promise<ProjectVersionRow[]> {
  return (await readAllVersions())
    .filter((v) => v.project_id === projectId)
    .sort((a, b) => b.version_number - a.version_number);
}
